import React, { useState, useEffect } from 'react';
import { 
  Inbox, 
  Search, 
  Mail, 
  MailOpen, 
  Archive, 
  Trash2, 
  Send,
  AlertCircle,
  Check,
  Loader2
} from 'lucide-react';
import { Message } from '../lib/types';
import { 
  getMessages, 
  markAsRead, 
  saveReply, 
  archiveMessage, 
  deleteMessage 
} from '../lib/messages';

const InboxPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [reply, setReply] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const fetchedMessages = await getMessages();
      setMessages(fetchedMessages);
    } catch (error) {
      setError('Erreur lors du chargement des messages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageSelect = async (message: Message) => {
    setSelectedMessage(message);
    if (!message.readAt) {
      try {
        await markAsRead(message.id);
        setMessages(messages.map(m => 
          m.id === message.id ? { ...m, readAt: new Date().toISOString() } : m
        ));
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleReply = async () => {
    if (!selectedMessage || !reply.trim()) return;

    setIsSending(true);
    try {
      await saveReply(selectedMessage.id, reply);
      
      setMessages(messages.map(m =>
        m.id === selectedMessage.id ? { ...m, reply, repliedAt: new Date().toISOString() } : m
      ));
      
      setSuccess('Réponse envoyée avec succès !');
      setReply('');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Erreur lors de l\'envoi de la réponse');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsSending(false);
    }
  };

  const handleArchive = async (messageId: string) => {
    try {
      await archiveMessage(messageId);
      setMessages(messages.map(m =>
        m.id === messageId ? { ...m, isArchived: true } : m
      ));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } catch (error) {
      setError('Erreur lors de l\'archivage du message');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.')) {
      return;
    }

    try {
      await deleteMessage(messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      setSuccess('Message supprimé avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Erreur lors de la suppression du message');
      setTimeout(() => setError(null), 3000);
    }
  };

  const filteredMessages = messages.filter(message => {
    const searchLower = searchTerm.toLowerCase();
    return (
      message.senderName.toLowerCase().includes(searchLower) ||
      message.senderEmail.toLowerCase().includes(searchLower) ||
      message.content.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex">
      {/* Messages List */}
      <div className="w-1/3 border-r bg-gray-50">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un message..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-y-auto h-[calc(100vh-5rem)]">
          {filteredMessages.map(message => (
            <div
              key={message.id}
              onClick={() => handleMessageSelect(message)}
              className={`p-4 border-b cursor-pointer transition-colors ${
                selectedMessage?.id === message.id
                  ? 'bg-indigo-50'
                  : 'hover:bg-gray-100'
              } ${!message.readAt ? 'bg-blue-50' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900">{message.senderName}</h3>
                <span className="text-sm text-gray-500">
                  {new Date(message.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-gray-600 truncate">{message.content}</p>
              <div className="flex items-center mt-2 text-xs text-gray-500">
                {message.readAt ? (
                  <MailOpen className="w-4 h-4 mr-1" />
                ) : (
                  <Mail className="w-4 h-4 mr-1" />
                )}
                <span>{message.senderEmail}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message Detail */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedMessage ? (
          <>
            <div className="p-4 border-b">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold">{selectedMessage.senderName}</h2>
                  <p className="text-gray-600">{selectedMessage.senderEmail}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleArchive(selectedMessage.id)}
                    className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
                    title="Archiver"
                  >
                    <Archive className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                Reçu le {new Date(selectedMessage.createdAt).toLocaleString()}
              </div>
            </div>

            <div className="flex-1 p-4 overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                {selectedMessage.content}
              </div>

              {selectedMessage.reply && (
                <div className="border-l-4 border-indigo-500 pl-4 mb-4">
                  <div className="text-sm text-gray-500 mb-2">
                    Réponse envoyée le {new Date(selectedMessage.repliedAt!).toLocaleString()}
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4">
                    {selectedMessage.reply}
                  </div>
                </div>
              )}
            </div>

            {!selectedMessage.reply && (
              <div className="p-4 border-t">
                {error && (
                  <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-md flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    <p>{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-4 bg-green-50 text-green-600 p-3 rounded-md flex items-center">
                    <Check className="w-5 h-5 mr-2" />
                    <p>{success}</p>
                  </div>
                )}

                <div className="flex space-x-4">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Écrivez votre réponse..."
                    className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={4}
                  />
                  <button
                    onClick={handleReply}
                    disabled={isSending || !reply.trim()}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 h-fit"
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Inbox className="w-12 h-12 mx-auto mb-4" />
              <p>Sélectionnez un message pour le consulter</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPage;